import { useMarketBaseToken } from '@/client/hooks/use-market-base-token';
import { useSingerTokenBalance } from '@/client/hooks/use-signed-token-balance';
import { useSignedUserAssets } from '@/client/hooks/use-signed-user-assets';
import { TradeType } from '@/client/store/trade-store';
import { api } from '@/client/utils/api';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { useUserPositions } from '@/libs/react-sdk/api/use-user-options';
import { useMemo } from 'react';

type BalanceProps = {
  type: TradeType;
  marketAddress: string;
  optionAddress?: string;
  uid: string;
};
export const useBalance = ({ type, uid, marketAddress, optionAddress }: BalanceProps) => {
  const userAssets = useSignedUserAssets(api);
  const market = useMarketDetail(api, { marketAddress }, { subscribe: false });
  // Balance must be denominated in the traded market's own base token, not
  // the global toggle token (mixed multi-base lists)
  const baseToken = useMarketBaseToken(market.data?.baseTokenAddress);
  const tokenBalances = useSingerTokenBalance(api);
  // Compare collateral addresses case-insensitively: a checksummed address from
  // the API would otherwise miss this exact-match find and silently fall back to
  // the wrong-currency balance. Lowercase is the canonical form across layers.
  const onChainBalance = tokenBalances.data?.find(
    item => item.baseTokenAddress?.toLowerCase() === baseToken.address.toLowerCase()
  );
  const params = useMemo(() => {
    return {
      baseTokenAddress: baseToken.address,
      page: 1,
      marketAddress,
      optionAddress,
      uid,
    };
  }, [baseToken.address, marketAddress, optionAddress, uid]);
  const userPositions = useUserPositions(api, params);
  const positions = useMemo(
    () => userPositions.data?.pages.flatMap(item => item.positions) || [],
    [userPositions.data?.pages]
  );
  const optionsPosition = useMemo(
    () => positions.find(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase()),
    [optionAddress, positions]
  );
  const data = useMemo(() => {
    if (type === 'buy') {
      if (onChainBalance?.balance !== undefined) return onChainBalance.balance;
      return userAssets.data?.balance !== undefined ? BigInt(userAssets.data?.balance) : undefined;
    } else {
      return optionsPosition?.balance !== undefined ? BigInt(optionsPosition?.balance) : undefined;
    }
  }, [type, onChainBalance?.balance, userAssets.data?.balance, optionsPosition?.balance]);
  const isLoading = useMemo(
    () => userAssets.isLoading || userPositions.isLoading || tokenBalances.isLoading,
    [userAssets.isLoading, userPositions.isLoading, tokenBalances.isLoading]
  );
  const buyDecimal = useMemo(
    () => onChainBalance?.baseTokenDecimals ?? userAssets.data?.decimal,
    [onChainBalance?.baseTokenDecimals, userAssets.data?.decimal]
  );
  const sellDecimal = useMemo(() => optionsPosition?.optionDecimal, [optionsPosition?.optionDecimal]);
  const decimal = useMemo(
    () => (type == 'buy' ? buyDecimal : sellDecimal) ?? baseToken.decimals,
    [buyDecimal, sellDecimal, type, baseToken.decimals]
  );

  return {
    data,
    decimal,
    buyDecimal,
    sellDecimal,
    isLoading,
    refetch: async () => {
      await Promise.all([userAssets.refetch(), userPositions.refetch(), tokenBalances.refetch()]);
    },
  };
};
